import React, { useState, useMemo } from "react";
import { Menu, ChevronDown, Settings, LogOut, Building2 } from "lucide-react";
import { useAgency } from "@/contexts/AgencyContext";
import { useAuth } from "@/contexts/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTrigger, SheetClose } from "../../sheet";
import { LeftNavigationData } from "@/data/LeftNavigation";
import { usePermissions } from "@/hooks/usePermissions";
import { X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../../popover";
import { APP_VERSION } from "@/config/version";

/**
 * Gera avatar com iniciais do usuário
 */
const generateUserAvatar = (name: string): string => {
  const initials = name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2)
  
  const backgroundColor = '#a1a1aa' // neutral-400
  const textColor = '#000000' // preto
  
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg width="40" height="40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="20" fill="${backgroundColor}"/>
      <text x="20" y="26" text-anchor="middle" fill="${textColor}" font-family="Arial" font-size="14" font-weight="bold">
        ${initials}
      </text>
    </svg>
  `)}`
}
/**
 * Componente de logout que limpa a sessão e redireciona
 */
const LogoutButton: React.FC<{ onLogout: () => Promise<void>; className?: string }> = ({ onLogout, className }) => {
  const handleLogout = async () => {
    console.log('🚪 [Navibar] Iniciando logout...')
    console.log('🗑️ [Navibar] Limpando sessão do localStorage...')
    await onLogout()
    // O redirecionamento será feito automaticamente pelo ProtectedRoute
  }

  return (
    <button
      onClick={handleLogout}
      className={className}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
      </svg>
      <p className="text-[14px] font-medium">Sair</p>
    </button>
  )
}
const Navibar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { agency } = useAgency();
  const { user, logout } = useAuth();
  const { hasPermission } = usePermissions();
  const [openUserMenu, setOpenUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpenUserMenu(false);
  };

  const handleManageAgency = () => {
    navigate('/agency');
    setOpenUserMenu(false);
  };

  const handleEditProfile = () => {
    navigate('/settings?tab=perfil');
    setOpenUserMenu(false);
  };

  const filteredMainNav = useMemo(() => {
    return LeftNavigationData.filter(item => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  }, [hasPermission]);

  const logoSrc = agency?.logo_url || "/logo-white.png";

  return (
    <div className="w-full min-h-[64px] md:px-5 px-4 border-b-[1px] border-[#3F3F464D] flex justify-between items-center">
      <div className="flex items-center">
        <img
          src={logoSrc}
          className="max-w-[140px] max-h-[40px] object-contain"
          alt={agency?.company_name || "Logo"}
          onError={(e) => {
            e.currentTarget.src = "/logo-white.png";
          }}
        />
      </div>
        <div className="flex items-center gap-3">
          <Popover open={openUserMenu} onOpenChange={setOpenUserMenu}>
            <PopoverTrigger asChild>
              <button className="hidden sm:flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer">
                <div className="flex flex-col items-end">
                  <p className="text-sm font-medium text-neutral-50">{user?.name}</p>
                  <p className="text-xs text-neutral-400">{user?.email}</p>
                </div>
                <div className="w-[40px] h-[40px] rounded-full overflow-hidden">
                  <img
                    src={user?.name ? generateUserAvatar(user.name) : "/user.svg"}
                    className="w-full h-full object-cover"
                    alt={user?.name || "User"}
                  />
                </div>
                <ChevronDown size={16} className="text-neutral-400" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="!bg-[#18181B] p-2 w-[220px] flex flex-col gap-1 border-[#3F3F464D] border mr-4">
              {user?.type === 'agency_user' && (
                <button
                  onClick={handleManageAgency}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-[7px] hover:bg-[#27272A] transition-colors text-neutral-50"
                >
                  <Building2 size={16} />
                  <span className="text-sm">Gerenciar Agência</span>
                </button>
              )}
              <button
                onClick={handleEditProfile}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-[7px] hover:bg-[#27272A] transition-colors text-neutral-50"
              >
                <Settings size={16} />
                <span className="text-sm">Editar Perfil</span>
              </button>
              <div className="h-[1px] bg-[#3F3F464D] my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-[7px] hover:bg-[#27272A] transition-colors text-red-400"
              >
                <LogOut size={16} />
                <span className="text-sm">Sair</span>
              </button>
            </PopoverContent>
          </Popover>
        </div>

        <div className="sm:hidden block">
          <Sheet>
            <SheetTrigger>
              <Menu size={24} color="#FAFAFA" />
            </SheetTrigger>
            <SheetContent>
              <SheetHeader className="w-full justify-center  items-center flex flex-col">
                <SheetClose className="w-full flex justify-end">
                  <X size={28} color="#FAFAFA" />
                </SheetClose>
                <div className="flex flex-col items-center gap-2 mt-4">
                  <div className="w-[100px] h-[100px] rounded-full overflow-hidden">
                    <img 
                      src={user?.name ? generateUserAvatar(user.name) : "/user.svg"}
                      className="w-full h-full object-cover"
                      alt={user?.name || "User"}
                    />
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-medium text-neutral-50">{user?.name}</p>
                    <p className="text-sm text-neutral-400">{user?.email}</p>
                  </div>
                </div>
              </SheetHeader>
              <div className="w-full flex gap-3 flex-col flex-1 overflow-y-auto">
                {filteredMainNav.map((item, index) => {
                  const isActive = item.path === location.pathname;

                  return (
                    <div key={index} className="relative flex px-5 w-full">
                      <SheetClose asChild>
                        <Link
                          to={item.path}
                          className={clsx(
                            "flex w-full gap-3 items-center px-3 min-h-[48px] rounded-[12px] transition-all border border-transparent duration-200 relative overflow-hidden group/item",
                            isActive
                              ? "bg-gradient-to-r border !border-red-500/30 from-red-900/20 to-red-600/20  text-red-200"
                              : "hover:bg-neutral-800/50 text-neutral-300 hover:text-neutral-100"
                          )}
                        >
                          <div
                            className={clsx(
                              "flex-shrink-0 transition-all duration-200 rounded-lg"
                            )}
                          >
                            {item.icon}
                          </div>

                          <p
                            className={clsx(
                              "text-[14px] font-medium transition-all duration-300 whitespace-nowrap"
                            )}
                          >
                            {item.title}
                          </p>
                        </Link>
                      </SheetClose>
                    </div>
                  );
                })}
                <div className="mt-4 px-5">
                  <SheetClose asChild>
                  <LogoutButton
                    onLogout={logout}
                    className="flex w-full gap-3 items-center px-3 min-h-[48px] rounded-[12px] transition-all border border-transparent duration-200 hover:bg-neutral-800/50 text-neutral-300 hover:text-neutral-100"
                  />
                  </SheetClose>
                </div>
                <div className="mt-auto pt-4 px-5 text-center text-neutral-400 text-xs">
                  v{APP_VERSION}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
    </div>
  );
};

export default Navibar;
